import React, { useEffect, useRef, useState } from "react";
import type * as PixiType from 'pixi.js';
import type * as pixiDisplay from "pixi-live2d-display";
import './Live2DLipSync.scss';

const PIXI_Inst = window.PIXI;

interface Live2DLipSyncProps {
    modelUrl: string;
    audioUrl: string;
    onChatSending: (chat: string) => Promise<string>;
    onPageAccepted: () => void;
}

const Live2DLipSync: React.FC<Live2DLipSyncProps> = ({ modelUrl, audioUrl, onChatSending, onPageAccepted }) => {
    const canvasWrapperRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const appRef = useRef<PixiType.Application | null>(null);
    const modelRef = useRef<pixiDisplay.Live2DModel | null>(null);
    const audioAnalyserRef = useRef<AnalyserNode | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const animationRef = useRef<number | null>(null);
    const [isOverlayVisible, setIsOverlayVisible] = useState(true);
    const [awaitUserInteract, setAwaitUserInteract] = useState({});
    const [chatMessage, setChatMessage] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState(true);

    const loadModelAndAudio = async (modelUrl: string, audioUrl: string, shouldLoadAudio = true) => {
        if (!audioUrl || audioUrl === "") return;

        const clearStage = () => {
            const stage = appRef.current.stage;
            if (stage) {
                stage.children.forEach(layer => layer.destroy());
            }
        };

        try {
            const model = await (window.PIXI as any).live2d.Live2DModel.from(modelUrl);
            model.scale.set(0.3);
            model.position.set(appRef.current.screen.width / 2, appRef.current.screen.height / 2);
            model.anchor.set(0.5);

            if (audioRef.current) {
                audioRef.current.pause();
            }

            clearStage();
            appRef.current.stage.addChild(model);
            appRef.current.start();

            modelRef.current = model;

            if (shouldLoadAudio) {
                await syncLipMovement(audioUrl, model);
            }
        } catch (error) {
            // console.error("Error loading Live2D model or audio:", error);
        }
    };

    const handleSendChat = async () => {
        setIsLoading(true);
        try {
            if (chatMessage.trim() && onChatSending) {
                await onChatSending(chatMessage);
            }
        } catch (error) {
            // console.error("Error sending message:", error);
        } finally {
            setIsLoading(false);
            setChatMessage("");
        }
    };

    const handleCloseOverlay = () => {
        setIsOverlayVisible(false);
        onPageAccepted();
    };

    useEffect(() => {
        if (!canvasRef.current) return;

        const app = new PIXI_Inst.Application({
            view: canvasRef.current,
            width: 180,
            height: 270,
            resizeTo: canvasWrapperRef.current,
            backgroundAlpha: 0,
            backgroundColor: 0xececec
        });

        appRef.current = app;
    }, []);

    useEffect(() => {
        loadModelAndAudio(modelUrl, audioUrl);
    }, [modelUrl, audioUrl]);

    useEffect(() => {
        const cache = awaitUserInteract as Live2DLipSyncProps;
        // check and sync
        if (!isOverlayVisible && cache.audioUrl) {
            loadModelAndAudio(cache.modelUrl, cache.audioUrl);
        }
    }, [isOverlayVisible]);

    const syncLipMovement = async (url: string, model: pixiDisplay.Live2DModel) => {
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`Failed to fetch audio: ${response.statusText}`);
        }

        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);

        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const audioSource = audioContext.createMediaElementSource(audio);

        audioSource.connect(analyser);
        analyser.connect(audioContext.destination);

        audioAnalyserRef.current = analyser;

        const startAnimation = () => {
            const dataArray = new Uint8Array(audioAnalyserRef.current.frequencyBinCount);
            const animate = () => {
                audioAnalyserRef.current.getByteFrequencyData(dataArray);
                const mouthOpenValue = Math.max(...dataArray) / 255;

                const time = performance.now() / 1000;
                const angle = Math.sin(time) * 15;

                (modelRef.current.internalModel.coreModel as any).setParameterValueById("ParamAngleX", angle);
                (modelRef.current.internalModel.coreModel as any).setParameterValueById("ParamAngleY", Math.cos(time) * 10);

                (modelRef.current.internalModel.coreModel as any).setParameterValueById("ParamMouthOpenY", mouthOpenValue);

                animationRef.current = requestAnimationFrame(animate);
            };

            animationRef.current = requestAnimationFrame(animate);
        };

        const stopAnimation = () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
        };

        const updateTransform = () => {
            if (modelRef.current && modelRef.current.parent) {
                modelRef.current.updateTransform();
            }
        }

        if (!isOverlayVisible) {
            try {
                appRef.current.ticker.add(updateTransform);
                startAnimation();

                audio.play();

                audio.addEventListener("ended", () => {
                    stopAnimation();
                    appRef.current.ticker.remove(updateTransform);
                });
            } catch (error) {
            }
        } else {
            setAwaitUserInteract({
                modelUrl,
                audioUrl
            });
        }
    };

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && !isLoading) {
            await handleSendChat();
        }
    };

    const canvasStyle: React.CSSProperties = {
        display: "block",
        width: "210px",
        height: "270px",
    };

    const overlayStyle: React.CSSProperties = {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: isOverlayVisible ? "flex" : "none",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
    };

    const buttonStyle: React.CSSProperties = {
        padding: "15px 30px",
        backgroundColor: "#F17A20",
        color: "white",
        border: "none",
        borderRadius: "25px",
        fontSize: "18px",
        cursor: "pointer",
        boxShadow: "0 2px 3px rgba(0, 0, 0, 0.1)",
    };

    const wrapperStyle: React.CSSProperties = {
        position: "fixed",
        bottom: "10px",
        left: "5px",
        padding: "2px",
        backgroundColor: "#ececec",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: "2px",
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    };

    const wrapperCloseStyle: React.CSSProperties = {
        position: "fixed",
        bottom: "17px",
        left: "5px",
        padding: "2px",
        cursor: 'pointer'
    };


    const chatInputStyle: React.CSSProperties = {
        flex: 1,
        padding: "8px",
        border: "1px solid #ccc",
        borderRadius: "5px",
        fontSize: "14px",
        width: "0px"
    };

    const sendButtonStyle: React.CSSProperties = {
        padding: "8px 15px",
        color: "#fff",
        border: "none",
        borderRadius: "5px",
        backgroundColor: isLoading ? "#aaa" : "#F17A20",
        cursor: isLoading ? "not-allowed" : "pointer",
    };

    const chatboxStyle: React.CSSProperties = {
        display: "flex",
        gap: "10px",
        paddingTop: "5px",
        paddingBottom: "5px"
    };

    return (
        <div className={'live2d-lip-sync'} style={isOpen ? wrapperStyle : wrapperCloseStyle} ref={canvasWrapperRef}>
            {
                !isOpen && <>
                    <button
                        className="flex items-center bg-blue-500 text-white px-4 py-2 p-4 rounded-full shadow-lg hover:bg-blue-600"
                        onClick={() => {
                            setIsOpen(true);
                            loadModelAndAudio(modelUrl, audioUrl, false);
                        }}>
                        <i className="fas fa-comment"></i>
                    </button>
                </>
            }

            <div className={isOpen ? 'show' : 'hide'}>
                <canvas ref={canvasRef} style={canvasStyle} />
                <div style={chatboxStyle}>
                    <input
                        type="text"
                        style={chatInputStyle}
                        value={chatMessage}
                        onChange={(e) => setChatMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading}
                        placeholder="Type your message..." />

                    <button
                        onClick={handleSendChat}
                        style={sendButtonStyle}
                        disabled={isLoading}>
                        {
                            isLoading ? (
                                <>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        className="w-4 h-4 animate-spin text-white">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                    </svg>
                                </>
                            ) : (
                                "Send"
                            )}
                    </button>
                </div>
                <div style={overlayStyle}>
                    <button style={buttonStyle} onClick={handleCloseOverlay}>
                        Open AI Assistant
                    </button>
                </div>

                <button
                    className="absolute top-2 right-2 pt-[5px] pb-[5px] pl-[10px] pr-[10px] bg-gray-200 text-white rounded-full hover:bg-gray-300 z-2000"
                    onClick={() => setIsOpen(false)}>
                    <i className="fas fa-times"></i>
                </button>
            </div>
        </div>
    );
};

export default Live2DLipSync;
